import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "actionsMenuProps", "buttonWrapperProps"];
import { Children, cloneElement, isValidElement, useCallback, useEffect, useRef, useState } from 'react';
import { ActionsMenu } from '../../../components/ActionsMenu';
import { ButtonWrapper } from '../ButtonWrapper';
import unescapedText from 'I18n/utils/unescapedText';
import { HiddenMeasure } from '../../utils/HiddenMeasure';
import { ActionsCollapserProvider, useActionsCollapserContext } from './ActionsCollapserProvider';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { View } from '../../../components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getElementWidth = el => {
  const rect = el.getBoundingClientRect();
  const computedStyles = getComputedStyle(el);
  const width = rect.width + parseFloat(computedStyles.marginLeft) + parseFloat(computedStyles.marginRight);
  return width;
};
const getAvailableWidth = (parentEl, outerEl) => {
  const computedStyles = getComputedStyle(parentEl, null);
  if (!computedStyles) return Infinity;
  const innerWidth = getElementWidth(parentEl) - parseFloat(computedStyles.paddingLeft) - parseFloat(computedStyles.paddingRight) - parseFloat(computedStyles.paddingTop) - parseFloat(computedStyles.paddingBottom);
  let availableWidth = innerWidth;
  if (computedStyles.display === 'flex' && computedStyles.flexDirection === 'row' && computedStyles.flexWrap === 'nowrap') {
    const childNodes = parentEl.childNodes;
    for (let i = 0; i < childNodes.length; i++) {
      if (childNodes[i] !== outerEl) availableWidth -= getElementWidth(childNodes[i]);
    }
  }
  return availableWidth;
};
const renderExcessActionsDropdown = (moreActionsRenderer, excessActions) => {
  return /*#__PURE__*/cloneElement(moreActionsRenderer(excessActions), {
    'data-more-tabs': true
  });
};
export function ActionsCollapser(_ref) {
  var _actionsCollapserCont, _actionsCollapserCont2;
  let {
      children,
      actionsMenuProps,
      buttonWrapperProps
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [excessActionsCount, setExcessActionsCount] = useState(0);
  const filterValidChildren = childNodes => {
    return Children.toArray(childNodes).filter(child => {
      if (child === null || typeof child === 'boolean') return false;
      if ( /*#__PURE__*/isValidElement(child)) {
        const element = child;
        if (element.props.children === null || Object.keys(element.props).length === 0) return false;
        const renderedOutput = element.props;
        if (renderedOutput === null) return false;
        return true;
      }
      return false;
    });
  };
  const childArray = filterValidChildren(children);
  const _outerEl = useRef(null);
  const _measureEl = useRef(null);
  const _resizeObserver = useRef(null);
  const actionsCollapserContext = useActionsCollapserContext();
  const actionsMenuStyleOverrides = deepMerge({
    inlineSize: 'fit-content'
  }, (_actionsCollapserCont = actionsCollapserContext === null || actionsCollapserContext === void 0 ? void 0 : actionsCollapserContext.actionsMenuStyleOverrides) !== null && _actionsCollapserCont !== void 0 ? _actionsCollapserCont : {});
  const buttonWrapperStyleOverrides = deepMerge({
    columnGap: 0
  }, (_actionsCollapserCont2 = actionsCollapserContext === null || actionsCollapserContext === void 0 ? void 0 : actionsCollapserContext.buttonWrapperStyleOverrides) !== null && _actionsCollapserCont2 !== void 0 ? _actionsCollapserCont2 : {});
  const stopObserver = useCallback(() => {
    var _resizeObserver$curre;
    (_resizeObserver$curre = _resizeObserver.current) === null || _resizeObserver$curre === void 0 || _resizeObserver$curre.disconnect();
  }, [_resizeObserver]);
  useEffect(() => {
    if (_outerEl.current) {
      var _outerEl$current;
      _resizeObserver.current = new ResizeObserver(getExcessActionsCount);
      const parentElement = (_outerEl$current = _outerEl.current) === null || _outerEl$current === void 0 || (_outerEl$current = _outerEl$current.parentElement) === null || _outerEl$current === void 0 ? void 0 : _outerEl$current.parentElement;
      if (parentElement) {
        _resizeObserver.current.observe(parentElement);
      }
      if (_measureEl.current) {
        _resizeObserver.current.observe(_measureEl.current);
      }
    }
    return stopObserver;
  });
  const moreActionsRenderer = excessActions => {
    const options = Children.toArray(excessActions).map(child => {
      if ( /*#__PURE__*/isValidElement(child)
      // typeof (child.props as any).children === 'string'
      ) {
        const childrenArray = Children.toArray(child.props.children);
        const indicator = childrenArray.length > 1 ? childrenArray[0] : null;
        const text = childrenArray.length > 1 ? childrenArray[1] : childrenArray[0];
        return {
          text,
          value: child.key,
          indicator
        };
      }
      return null;
    }).filter(option => option !== null);
    const largestElementWidth = Math.max(...Children.toArray(excessActions).map(child => {
      if ( /*#__PURE__*/isValidElement(child)) {
        const element = document.createElement('div');
        element.style.visibility = 'hidden';
        element.style.position = 'absolute';
        element.style.whiteSpace = 'nowrap';
        element.style.fontSize = 'inherit';
        element.style.fontFamily = 'inherit';
        element.innerText = child.props.children;
        document.body.appendChild(element);
        const width = element.offsetWidth + 32;
        document.body.removeChild(element);
        return width;
      }
      return 0;
    }));
    return /*#__PURE__*/_jsx(ActionsCollapserProvider, {
      actionsMenuStyleOverrides: actionsMenuStyleOverrides,
      children: /*#__PURE__*/_jsx(ActionsMenu, Object.assign({
        onActionClick: () => {},
        use: "tertiary-light",
        "data-more-tabs": true,
        actions: options,
        referenceText: unescapedText('salesUI.UITabs.moreDropdownLabel'),
        styleOverrides: actionsMenuStyleOverrides,
        "aria-label": "More",
        menuInlineSize: largestElementWidth
      }, actionsMenuProps))
    });
  };
  function getExcessActionsCount() {
    const measureEl = _measureEl.current;
    const outerEl = _outerEl && _outerEl.current ? _outerEl.current.parentElement : null;
    const parentEl = outerEl ? outerEl.parentElement : null;
    if (measureEl == null || outerEl == null || parentEl == null) {
      return;
    }
    const horizontalGap = _outerEl.current ? Math.abs(parseInt(getComputedStyle(_outerEl.current).marginLeft, 10)) : 0;
    const containerWidth = Math.ceil(getAvailableWidth(parentEl, outerEl));
    let totalActionsWidth = 0;
    const totalActionsArray = Array.from(measureEl.children).filter(action => !action.getAttribute('data-more-tabs'));

    // Don't add in the horizontal gap for zero-width elements
    const getElementWidthPlusHorizontalGap = element => {
      let elementWidth = getElementWidth(element);
      if (elementWidth > 0) {
        elementWidth += horizontalGap;
      }
      return elementWidth;
    };
    totalActionsArray.forEach(element => {
      totalActionsWidth += getElementWidthPlusHorizontalGap(element);
    });
    if (totalActionsWidth <= containerWidth) {
      if (excessActionsCount !== 0) setExcessActionsCount(0);
      return;
    }
    const moreDropDownElement = document.querySelector('[data-more-tabs]');
    const moreDropDown = moreDropDownElement ? getElementWidth(moreDropDownElement) + horizontalGap : 0;
    let calcActionsWidth = moreDropDown;
    let newExcessActionsCount = totalActionsArray.length;
    for (let i = 0; i < totalActionsArray.length; i++) {
      const actionWidth = getElementWidthPlusHorizontalGap(totalActionsArray[i]);
      if (calcActionsWidth + actionWidth > containerWidth) break;
      calcActionsWidth += actionWidth;
      newExcessActionsCount -= 1;
    }
    if (newExcessActionsCount !== excessActionsCount) {
      setExcessActionsCount(newExcessActionsCount);
    }
  }
  const getExcessActions = () => {
    const excessActions = childArray.slice(childArray.length - excessActionsCount);
    return excessActions;
  };
  const getVisibleActions = () => {
    const visibleActions = childArray.slice(0, childArray.length - excessActionsCount);
    return visibleActions;
  };
  const visibleActions = getVisibleActions();
  const excessActions = getExcessActions();
  const excessActionsDropdown = renderExcessActionsDropdown(moreActionsRenderer, excessActions);
  buttonWrapperProps = Object.assign({}, buttonWrapperProps, {
    flexRef: _outerEl
  });
  const spanWrappedChildren = Children.map(children, (child, index) => {
    return /*#__PURE__*/_jsx("span", {
      children: child
    }, index);
  });
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(View, Object.assign({}, cssStyles, domProps, {
    children: /*#__PURE__*/_jsx(ActionsCollapserProvider, {
      buttonWrapperStyleOverrides: buttonWrapperStyleOverrides,
      children: /*#__PURE__*/_jsxs(ButtonWrapper, Object.assign({}, buttonWrapperProps, {
        children: [visibleActions, excessActionsCount > 0 && excessActionsDropdown, /*#__PURE__*/_jsxs(HiddenMeasure, {
          ref: _measureEl,
          style: {
            height: 0
          },
          className: "hidden-measure",
          children: [spanWrappedChildren, excessActionsDropdown]
        })]
      }))
    })
  }));
}